import type { EventTier, SeasonDefinition } from './types';
import { builtInCourseIds } from '../course/data/builtInCourses';

const schedule = [
  ['Pine Valley Invitational', 'tournament-pines', 'Tournament Pines'],
  ['Copper Ridge Championship', 'copper-ridge', 'Copper Ridge'],
  ['Emerald Coast Open', 'emerald-coast', 'Emerald Coast'],
  ['Bluewater Classic', 'bluewater-bay', 'Bluewater Bay'],
  ['Ironwood Heritage', 'ironwood-forest', 'Ironwood Forest'],
  ['Heartland Championship', 'golden-prairie', 'Golden Prairie'],
  ['Summit Signature', 'summit-lake', 'Summit Lake'],
  ['Canyon National', 'redstone-canyon', 'Redstone Canyon'],
  ['Royal Heather Championship', 'royal-heather', 'Royal Heather'],
  ['Aurora Invitational', 'aurora-cliffs', 'Aurora Cliffs'],
  ['Black Oak Classic', 'black-oak-reserve', 'Black Oak Reserve'],
  ['Pelican Shores Open', 'pelican-shores', 'Pelican Shores'],
  ['Glacier Peaks Championship', 'glacier-peaks', 'Glacier Peaks'],
  ['Windhaven Signature', 'windhaven-links', 'Windhaven Links'],
  ['Crystal Lakes Invitational', 'crystal-lakes', 'Crystal Lakes'],
  ['Sandstone National', 'sandstone-dunes', 'Sandstone Dunes'],
  ['Storm Point Championship', 'storm-point', 'Storm Point'],
  ['Crown Major Championship', 'eagle-crest-national', 'Eagle Crest National'],
  ['World Heritage Major', 'kings-moor', "King's Moor"],
  ['Tournament Golf Championship', 'champions-reserve', 'Champions Reserve'],
] as const;
const tierFor = (i: number): EventTier =>
  i === 19
    ? 'seasonChampionship'
    : [6, 13, 16].includes(i)
      ? 'signature'
      : [8, 17, 18].includes(i)
        ? 'major'
        : 'standard';
const qualificationFor = (tier: EventTier) =>
  tier === 'standard'
    ? { type: 'open' as const }
    : tier === 'signature'
      ? {
          type: 'any' as const,
          rules: [
            { type: 'seasonPointsRank' as const, threshold: 40 },
            { type: 'currentSeasonWin' as const },
            { type: 'topTenCount' as const, threshold: 2 },
          ],
        }
      : tier === 'major'
        ? {
            type: 'any' as const,
            rules: [
              { type: 'careerRanking' as const, threshold: 50 },
              { type: 'seasonPointsRank' as const, threshold: 30 },
              { type: 'currentSeasonWin' as const },
              { type: 'majorWinner' as const },
            ],
          }
        : { type: 'seasonChampionshipField' as const, threshold: 30 };

const finalQualificationFor = (index: number, tier: EventTier) => {
  if (index === 16)
    return {
      type: 'any' as const,
      rules: [
        { type: 'seasonPointsRank' as const, threshold: 35 },
        { type: 'currentSeasonWin' as const },
        { type: 'topTenCount' as const, threshold: 3 },
        { type: 'careerRanking' as const, threshold: 40 },
      ],
    };
  if (index === 17)
    return {
      type: 'any' as const,
      rules: [
        { type: 'careerRanking' as const, threshold: 45 },
        { type: 'seasonPointsRank' as const, threshold: 25 },
        { type: 'currentSeasonWin' as const },
        { type: 'majorWinner' as const },
        { type: 'priorEventFinish' as const, threshold: 5 },
      ],
    };
  if (index === 18)
    return {
      type: 'any' as const,
      rules: [
        { type: 'careerRanking' as const, threshold: 40 },
        { type: 'seasonPointsRank' as const, threshold: 20 },
        { type: 'currentSeasonWin' as const },
        { type: 'majorWinner' as const },
        { type: 'priorEventFinish' as const, threshold: 10 },
      ],
    };
  return qualificationFor(tier);
};

const middleSeasonPresentation = [
  {
    accentColor: '#71c9e8',
    mark: 'PELICAN',
    subtitle: 'Coastal precision',
    loadingCopy: 'Angles on the ocean breeze',
    courseIdentity: 'Broad tropical wetlands and lagoons',
    signatureHoles: [4, 5, 8, 18],
  },
  {
    accentColor: '#92bad8',
    mark: 'GLACIER',
    subtitle: 'Golf above the clouds',
    loadingCopy: 'Judge every climb and fall',
    courseIdentity: 'The tour’s highest alpine venue',
    signatureHoles: [4, 9, 13, 18],
  },
  {
    accentColor: '#9bb0bb',
    mark: 'WINDHAVEN',
    subtitle: 'The wind writes the strategy',
    loadingCopy: 'Keep it low. Find the running line.',
    courseIdentity: 'Traditional firm coastal links',
    signatureHoles: [3, 4, 12, 18],
  },
  {
    accentColor: '#84b8d5',
    mark: 'CRYSTAL',
    subtitle: 'Choose the waterline',
    loadingCopy: 'Every inlet changes the angle',
    courseIdentity: 'Reflective inland lakes and streams',
    signatureHoles: [4, 5, 12, 18],
  },
  {
    accentColor: '#cfaa6d',
    mark: 'SANDSTONE',
    subtitle: 'Width is a decision',
    loadingCopy: 'Pick a dune corridor and commit',
    courseIdentity: 'Open desert dunes and native sand',
    signatureHoles: [2, 6, 15, 18],
  },
] as const;
export const INAUGURAL_SEASON: SeasonDefinition = {
  id: 'season-1',
  name: 'Inaugural Tour Season',
  events: schedule.map(([name, courseId, courseName], i) => {
    const tier = tierFor(i);
    const noCut = tier === 'signature' || tier === 'seasonChampionship';
    return {
      id: `event-${i + 1}`,
      name,
      courseId,
      courseName,
      scheduleIndex: i,
      tier,
      fieldSize: tier === 'seasonChampionship' ? 30 : tier === 'signature' ? 48 : 72,
      rounds: 4,
      cutRule: { enabled: !noCut, afterRound: 2, topAndTies: 65 },
      qualificationRule: finalQualificationFor(i, tier),
      pointsMultiplier: { standard: 1, signature: 1.3, major: 1.5, seasonChampionship: 1.6 }[tier],
      rankingStrength: { standard: 1, signature: 1.25, major: 1.5, seasonChampionship: 1.6 }[tier],
      purse: 0,
      greenSpeedProfile: tier === 'major' ? 'championship' : 'tour',
      windRange: [3, 18],
      firmness: tier === 'major' || tier === 'seasonChampionship' ? 'firm' : 'normal',
      status: 'scheduled',
      availableCourseStatus: builtInCourseIds.has(courseId) ? 'available' : 'comingSoon',
      presentation:
        i >= 16
          ? {
              accentColor: ['#6e8290', '#b79a55', '#a78b51', '#c6a75a'][i - 16],
              mark: ['STORM POINT', 'CROWN MAJOR', 'WORLD HERITAGE', 'TOURNAMENT GOLF'][i - 16],
              subtitle: [
                'No shelter. Control the flight.',
                'The complete examination',
                'Old ground. Endless wind.',
                'Thirty players. One season title.',
              ][i - 16],
              loadingCopy: [
                'The headland wind is rising',
                'A national championship awaits',
                'Read the ancient slopes',
                'The season comes down to this',
              ][i - 16],
              courseIdentity: [
                'Raw exposed coastal headland',
                'Formal national championship golf',
                'Ancient high moorland',
                'Purpose-built season finale',
              ][i - 16],
              signatureHoles: [
                [3, 7, 13, 18],
                [6, 11, 15, 18],
                [2, 8, 13, 18],
                [4, 8, 12, 15, 16, 17, 18],
              ][i - 16],
            }
          : i >= 11 && i <= 15
            ? {
                ...middleSeasonPresentation[i - 11],
                signatureHoles: [...middleSeasonPresentation[i - 11].signatureHoles],
              }
            : undefined,
    };
  }),
};
